"use client";
import { Divider, Drawer, DrawerProps, IconButton, Stack, styled, ClickAwayListener, Typography, SxProps, Theme } from "@mui/material";
import React, { ReactElement, ReactNode } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTheme } from "../../Theme/Theme";
import { GrPowerReset } from "react-icons/gr";

type WcSideSheetComponentProps = DrawerProps & {
  children: ReactNode;
  drawerOpen: boolean;
  drawerTitle?: string;
  drawerActions?: ReactElement<any>;
  stackSx?: SxProps<Theme>;
  /**
   * @deprecated use the stackSx and the drawerSx props instead
   */
  sx?: SxProps<Theme>;
  drawerSx?: SxProps<Theme>;
  width?: number;
  maxWidth?: number;
  minWidth?: number;
  useClickAwayOff?: boolean;
  resetTitle?: string;
  hideOverflow?: boolean;
  height?: any;
  appBarHeight?: number;
  onDrawerClosed: () => void;
};

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-start",
}));

export const WcSideSheetComponent = ({
  children,
  drawerOpen,
  drawerTitle,
  drawerActions,
  stackSx,
  sx,
  drawerSx,
  hideOverflow,
  height,
  useClickAwayOff,
  onDrawerClosed,
  width,
  maxWidth,
  resetTitle,
  minWidth,
  appBarHeight,
  ...props
}: WcSideSheetComponentProps) => {
  const defaultDrawerWidth = width || 300;
  const minDrawerWidth = minWidth || 300;
  const maxDrawerWidth = maxWidth || 1500;
  const [drawerWidth, setDrawerWidth] = React.useState(defaultDrawerWidth);
  const theme = useTheme();

  const handleMouseDown = () => {
    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseUp = () => {
    document.removeEventListener("mouseup", handleMouseUp, true);
    document.removeEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseMove = React.useCallback((e: any) => {
    const offsetRight = document.body.offsetWidth - (e.clientX - document.body.offsetLeft);
    if (offsetRight > minDrawerWidth && offsetRight < maxDrawerWidth) {
      setDrawerWidth(offsetRight);
    }
  }, []);

  // for backward compatibility
  if (sx !== undefined) {
    stackSx = sx;
  }

  return (
    <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={() => (useClickAwayOff ? null : onDrawerClosed())}>
      <Drawer
        sx={[
          {
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              height: height ? height : appBarHeight ? `calc(100% - ${appBarHeight}px)` : "100%",
              top: appBarHeight || 0,
            },
          },
          ...(Array.isArray(drawerSx) ? drawerSx : [drawerSx]),
        ]}
        anchor="right"
        onClose={onDrawerClosed}
        open={drawerOpen}
        {...props}
      >
        <DrawerHeader>
          <IconButton onClick={onDrawerClosed}>
            {
              //@ts-ignore
              theme.direction === "ltr" ? <ChevronRightIcon /> : <ChevronLeftIcon />
            }
          </IconButton>
          {drawerTitle && (
            <Stack width={"100%"}>
              <Typography textAlign={"center"}>{drawerTitle}</Typography>
            </Stack>
          )}
          <Stack justifyContent={"flex-end"} direction={"row"} width={"100%"} spacing={2}>
            {drawerActions}
            <IconButton onClick={() => setDrawerWidth(defaultDrawerWidth)}>
              <GrPowerReset title={resetTitle || "reset sheet"} size={15} />
            </IconButton>
          </Stack>
        </DrawerHeader>

        <div
          onMouseDown={(e) => handleMouseDown()}
          style={{
            width: "5px",
            cursor: "ew-resize",
            padding: "4px 0 0",
            borderTop: "1px solid #ddd",
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            zIndex: 100,
            backgroundColor: "#f4f7f9",
          }}
        />
        <Divider />
        <Stack id="SideSheetCompomentInnerStack" width="100%" sx={[...(Array.isArray(stackSx) ? stackSx : [stackSx])]}>
          {children}
        </Stack>
      </Drawer>
    </ClickAwayListener>
  );
};

export default WcSideSheetComponent;
